/**
 * ElementStatsChart - Core Component
 *
 * Интерактивный график статистики по новым элементам.
 * Показывает количество созданных элементов за каждый период.
 * Использует Mantine AreaChart с Points Labels.
 *
 * Props:
 * - period: 'month' | '3months' | '6months' | 'year'
 * - granularity: 'daily' | 'weekly' | 'monthly'
 * - visibleTypes: string[] - массив кодов видимых типов элементов
 * - onPeriodChange: (period) => void
 * - onGranularityChange: (granularity) => void
 * - onTypeToggle: (typeCode) => void
 * - height: number - высота графика
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Group,
  Stack,
  Text,
  Loader,
  Center,
  Alert,
  SegmentedControl,
  Checkbox,
  Paper,
  SimpleGrid,
} from '@mantine/core';
import { AreaChart } from '@mantine/charts';
import { IconAlertCircle } from '@tabler/icons-react';
import tokens from '../../theme/designTokens';
import {
  ELEMENT_TYPES,
  PERIOD_OPTIONS,
  GRANULARITY_OPTIONS,
} from '../../constants/elementTypes';

// API для получения данных
const reportsApi = {
  async getElementStatsData(period, granularity) {
    const params = new URLSearchParams({ period, granularity });
    const response = await fetch(`/api/reports/element-stats?${params}`, {
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('У вас нет доступа к отчётам');
      }
      throw new Error('Не удалось загрузить данные графика');
    }

    return response.json();
  },
};

const ElementStatsChart = ({
  period = 'month',
  granularity = 'daily',
  visibleTypes = null,
  onPeriodChange,
  onGranularityChange,
  onTypeToggle,
  onSelectAll,
  height = 400,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState([]);

  // Внутреннее состояние для видимости (если не управляется извне)
  const [internalVisibleTypes, setInternalVisibleTypes] = useState(
    ELEMENT_TYPES.map((t) => t.code)
  );

  // Используем внешнее или внутреннее состояние
  const activeTypes = visibleTypes ?? internalVisibleTypes;

  // Загрузка данных
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await reportsApi.getElementStatsData(period, granularity);

      // Преобразуем данные для AreaChart
      const transformed = data.snapshots.map((snapshot) => {
        const date = new Date(snapshot.date);
        const formattedDate = date.toLocaleDateString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          timeZone: 'Europe/Moscow',
        });

        const point = { date: formattedDate };

        // Добавляем данные по каждому типу элемента
        ELEMENT_TYPES.forEach((type) => {
          point[type.code] = snapshot.element_counts[type.code] || 0;
        });

        return point;
      });

      setChartData(transformed);
    } catch (err) {
      console.error('Failed to fetch element stats data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [period, granularity]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Проверка состояния "все выбрано"
  const allSelected = activeTypes.length === ELEMENT_TYPES.length;
  const someSelected = activeTypes.length > 0 && !allSelected;

  // Обработчик переключения видимости
  const handleTypeToggle = (typeCode) => {
    if (onTypeToggle) {
      onTypeToggle(typeCode);
    } else {
      setInternalVisibleTypes((prev) =>
        prev.includes(typeCode)
          ? prev.filter((t) => t !== typeCode)
          : [...prev, typeCode]
      );
    }
  };

  // Обработчик "Выделить все"
  const handleSelectAll = () => {
    const newSelectAll = !allSelected;
    if (onSelectAll) {
      onSelectAll(newSelectAll);
    } else {
      if (newSelectAll) {
        setInternalVisibleTypes(ELEMENT_TYPES.map((t) => t.code));
      } else {
        setInternalVisibleTypes([]);
      }
    }
  };

  // Формируем series для AreaChart (только видимые типы)
  const series = ELEMENT_TYPES.filter((type) => activeTypes.includes(type.code)).map(
    (type) => ({
      name: type.code,
      color: type.color,
      label: type.name,
    })
  );

  if (loading) {
    return (
      <Center py="xl" style={{ minHeight: height }}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert
        icon={<IconAlertCircle size={16} />}
        title="Ошибка загрузки"
        color="red"
        variant="light"
      >
        {error}
      </Alert>
    );
  }

  if (chartData.length === 0) {
    return (
      <Center py="xl" style={{ minHeight: height }}>
        <Stack align="center" gap="sm">
          <Text c="dimmed" size="lg">Нет данных за выбранный период</Text>
          <Text c="dimmed" size="sm">
            Снимки создаются автоматически каждые 15 минут.
            Первые данные появятся после создания первого снимка.
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="md">
      {/* Панель управления */}
      <Group justify="space-between" wrap="wrap" gap="sm">
        {/* Период */}
        <Group gap="xs">
          <Text size="sm" fw={500}>
            Период:
          </Text>
          <SegmentedControl
            value={period}
            onChange={onPeriodChange}
            size="xs"
            radius="xl"
            data={PERIOD_OPTIONS}
          />
        </Group>

        {/* Гранулярность */}
        <Group gap="xs">
          <Text size="sm" fw={500}>
            Группировка:
          </Text>
          <SegmentedControl
            value={granularity}
            onChange={onGranularityChange}
            size="xs"
            radius="xl"
            data={GRANULARITY_OPTIONS}
          />
        </Group>
      </Group>

      {/* График */}
      <Paper
        p="md"
        radius={16}
        style={{ border: `1px solid ${tokens.colors.neutral[3]}` }}
      >
        <AreaChart
          h={height}
          data={chartData}
          dataKey="date"
          series={series}
          curveType="monotone"
          withLegend={false}
          withPointLabels
          pointLabelProps={{ fontSize: 10 }}
          gridAxis="xy"
          tickLine="xy"
          strokeWidth={2}
          dotProps={{ r: 4 }}
          activeDotProps={{ r: 6 }}
          fillOpacity={0.1}
          tooltipAnimationDuration={200}
          valueFormatter={(value) => value.toLocaleString('ru-RU')}
        />
      </Paper>

      {/* Легенда с чекбоксами */}
      <Paper
        p="md"
        radius={16}
        style={{
          border: `1px solid ${tokens.colors.neutral[3]}`,
          backgroundColor: tokens.colors.neutral[1],
        }}
      >
        <Group justify="space-between" mb="sm">
          <Text size="sm" fw={500}>
            Фильтр элементов:
          </Text>
          <Checkbox
            label={<Text size="sm" fw={500}>Выделить все</Text>}
            checked={allSelected}
            indeterminate={someSelected}
            onChange={handleSelectAll}
            size="xs"
            radius="xl"
          />
        </Group>
        <SimpleGrid cols={{ base: 2, xs: 3, sm: 4 }} spacing="xs">
          {/* Чекбоксы типов элементов */}
          {ELEMENT_TYPES.map((type) => (
            <Checkbox
              key={type.code}
              label={
                <Group gap={6}>
                  <Box
                    style={{
                      width: 12,
                      height: 12,
                      backgroundColor: type.color,
                      borderRadius: 2,
                    }}
                  />
                  <Text size="sm">{type.name}</Text>
                </Group>
              }
              checked={activeTypes.includes(type.code)}
              onChange={() => handleTypeToggle(type.code)}
              size="xs"
              radius="xl"
            />
          ))}
        </SimpleGrid>
      </Paper>
    </Stack>
  );
};

ElementStatsChart.propTypes = {
  period: PropTypes.oneOf(['month', '3months', '6months', 'year']),
  granularity: PropTypes.oneOf(['daily', 'weekly', 'monthly']),
  visibleTypes: PropTypes.arrayOf(PropTypes.string),
  onPeriodChange: PropTypes.func,
  onGranularityChange: PropTypes.func,
  onTypeToggle: PropTypes.func,
  onSelectAll: PropTypes.func,
  height: PropTypes.number,
};

export default ElementStatsChart;
